/**
 * Renderers for remote players, enemies and loot.  Each renderer keeps a
 * small map from id → BillboardHandle so the world-state update can just
 * patch positions / labels and let the renderer create or destroy sprites
 * as entities come and go.
 *
 * Issue #5 billboard rule reused:  BILLBOARDMODE_Y, NEAREST sampling,
 * alphaCutOff for cutout.  Players reuse the same pixel-art NPC sprite
 * for now (Issue #5); the renderer swaps in a different sprite for the
 * enemy (Issue #10) and the loot chest (Issue #12).
 */

import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Scene } from '@babylonjs/core/scene';

import { buildBillboard, BillboardHandle } from '../scene/npc';
import { enemySpriteTexture, chestSpriteTexture, npcSpriteTexture } from '../scene/textures';
import type { EnemyState, LootState, PlayerState } from '@hurka/shared';

export class PlayerRenderer {
  private billboards = new Map<string, BillboardHandle>();
  constructor(private scene: Scene) {}

  apply(myId: string, players: PlayerState[]): void {
    const seen = new Set<string>();
    for (const p of players) {
      if (p.playerId === myId) continue; // never render ourselves
      seen.add(p.playerId);
      let b = this.billboards.get(p.playerId);
      if (!b) {
        b = buildBillboard(this.scene, {
          name: `player-${p.playerId}`,
          texture: npcSpriteTexture(this.scene),
          position: new Vector3(p.x, p.y, p.z),
          height: 1.9,
          alphaCutoff: 0.5,
        });
        this.billboards.set(p.playerId, b);
      }
      b.setPosition(new Vector3(p.x, p.y, p.z));
      b.setLabel(p.name ?? '???');
    }
    // Remove billboards for players no longer present.
    for (const [id, b] of this.billboards) {
      if (!seen.has(id)) {
        b.dispose();
        this.billboards.delete(id);
      }
    }
  }

  remove(id: string): void {
    const b = this.billboards.get(id);
    if (b) {
      b.dispose();
      this.billboards.delete(id);
    }
  }

  clear(): void {
    for (const b of this.billboards.values()) b.dispose();
    this.billboards.clear();
  }
}

export class EnemyRenderer {
  private billboard: BillboardHandle | null = null;

  constructor(private scene: Scene) {}

  apply(state: EnemyState | null): void {
    if (!state || state.state === 'dead') {
      if (this.billboard) {
        this.billboard.dispose();
        this.billboard = null;
      }
      return;
    }
    if (!this.billboard) {
      this.billboard = buildBillboard(this.scene, {
        name: `enemy-${state.enemyId}`,
        texture: enemySpriteTexture(this.scene),
        position: new Vector3(state.x, state.y, state.z),
        height: 1.9,
        alphaCutoff: 0.5,
      });
    }
    this.billboard.setPosition(new Vector3(state.x, state.y, state.z));
    const hp = `${state.hp}/${state.maxHp}`;
    this.billboard.setLabel(`Goblin (${hp})`);
  }

  clear(): void {
    if (this.billboard) {
      this.billboard.dispose();
      this.billboard = null;
    }
  }
}

export class LootRenderer {
  private billboards = new Map<string, BillboardHandle>();
  constructor(private scene: Scene) {}

  apply(loots: LootState[]): void {
    const seen = new Set<string>();
    for (const l of loots) {
      if (l.claimedBy) continue; // fading out
      seen.add(l.lootId);
      let b = this.billboards.get(l.lootId);
      if (!b) {
        b = buildBillboard(this.scene, {
          name: `loot-${l.lootId}`,
          texture: chestSpriteTexture(this.scene),
          position: new Vector3(l.x, l.y + 0.4, l.z),
          height: 0.6,
          alphaCutoff: 0.5,
        });
        this.billboards.set(l.lootId, b);
      }
      b.setPosition(new Vector3(l.x, l.y + 0.4, l.z));
      b.setLabel(null);
    }
    for (const [id, b] of this.billboards) {
      if (!seen.has(id)) {
        b.dispose();
        this.billboards.delete(id);
      }
    }
  }

  remove(lootId: string): void {
    const b = this.billboards.get(lootId);
    if (b) {
      b.dispose();
      this.billboards.delete(lootId);
    }
  }

  clear(): void {
    for (const b of this.billboards.values()) b.dispose();
    this.billboards.clear();
  }
}
